import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Mail, Lock, User as UserIcon, Loader2, ArrowRight, Zap, Trophy, Brain, Plane } from "lucide-react";
import { toast } from "sonner";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>
);

export default function Auth() {
  const navigate = useNavigate();
  const { user, applyAuth } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { if (user) navigate("/app"); }, [user, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const { data } = await api.post(endpoint, form);
      applyAuth(data.token, data.user);
      toast.success(mode === "register" ? "Welcome to LyfePilot!" : "Welcome back!");
      navigate(data.user.onboarded ? "/app" : "/onboarding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  const guest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/guest");
      applyAuth(data.token, data.user);
      navigate("/onboarding");
    } catch { toast.error("Could not start guest mode"); }
    finally { setBusy(false); }
  };

  const forgot = async () => {
    if (!form.email) return toast.error("Enter your email first");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: form.email });
      if (data.demo_reset_token) {
        toast.info("Demo reset token generated (no email service in demo).", {
          description: `Token: ${data.demo_reset_token}`, duration: 8000,
        });
      } else toast.success(data.message);
    } catch { toast.error("Could not process request"); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen aurora bg-background text-foreground flex flex-col lg:flex-row">
      {/* Hero */}
      <div className="lg:w-1/2 relative overflow-hidden flex flex-col justify-center px-8 py-14 lg:px-16">
        <div className="absolute -top-32 -left-20 h-80 w-80 rounded-full bg-indigo-600/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative z-10 max-w-lg">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 grid place-items-center shadow-xl shadow-indigo-500/40">
              <Plane className="text-white" size={24} />
            </div>
            <span className="font-display font-extrabold text-2xl tracking-tight">Lyfe<span className="text-indigo-400">Pilot</span></span>
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tighter mb-5">
            Don't figure out life <span className="bg-gradient-to-r from-indigo-400 via-violet-400 to-emerald-400 bg-clip-text text-transparent">the hard way.</span>
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-10">
            Your co-pilot for real adult life. Learn money, career, housing, health and law through interactive simulations, an AI coach, and RPG-style progression.
          </p>
          <div className="grid grid-cols-3 gap-3 max-w-md">
            {[{ i: Zap, t: "Life Sims", c: "text-cyan-400" }, { i: Brain, t: "AI Coach", c: "text-violet-400" }, { i: Trophy, t: "Level Up", c: "text-yellow-400" }].map((f, idx) => (
              <div key={idx} className="glass rounded-2xl p-4 text-center">
                <f.i className={`mx-auto mb-2 ${f.c}`} size={26} />
                <span className="text-xs font-semibold">{f.t}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Auth card */}
      <div className="lg:w-1/2 flex items-center justify-center px-6 py-10">
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="glass rounded-3xl p-8 w-full max-w-md">
          <div className="flex gap-2 p-1 rounded-full bg-white/5 mb-7">
            {["login", "register"].map((m) => (
              <button key={m} data-testid={`tab-${m}`} onClick={() => setMode(m)}
                className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition-colors ${mode === m ? "bg-indigo-600 text-white" : "text-muted-foreground hover:text-foreground"}`}>
                {m === "login" ? "Sign In" : "Create Account"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Name</Label>
                <div className="relative mt-1.5">
                  <UserIcon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input data-testid="name-input" value={form.name} onChange={set("name")} required placeholder="Alex Rivera" className="pl-10 rounded-2xl h-12 bg-white/5 border-white/10" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</Label>
              <div className="relative mt-1.5">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="email-input" type="email" value={form.email} onChange={set("email")} required placeholder="you@email.com" className="pl-10 rounded-2xl h-12 bg-white/5 border-white/10" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</Label>
              <div className="relative mt-1.5">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="password-input" type="password" value={form.password} onChange={set("password")} required placeholder="••••••••" className="pl-10 rounded-2xl h-12 bg-white/5 border-white/10" />
              </div>
            </div>
            {mode === "login" && (
              <button type="button" onClick={forgot} data-testid="forgot-password-btn" className="text-xs text-indigo-400 hover:text-indigo-300 font-semibold">Forgot password?</button>
            )}
            <Button data-testid="submit-auth-btn" type="submit" disabled={busy} className="w-full h-12 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-base transition-all hover:-translate-y-0.5 active:scale-95">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <>{mode === "register" ? "Create Account" : "Sign In"} <ArrowRight size={18} className="ml-1" /></>}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <div className="space-y-3">
            <Button data-testid="google-login-btn" onClick={google} variant="outline" className="w-full h-12 rounded-2xl bg-white/5 border-white/10 hover:bg-white/10 font-semibold gap-2">
              <GoogleIcon /> Continue with Google
            </Button>
            <Button data-testid="guest-login-btn" onClick={guest} disabled={busy} variant="ghost" className="w-full h-12 rounded-2xl hover:bg-white/5 font-semibold text-muted-foreground">
              Continue as Guest
            </Button>
          </div>
          <p className="text-[11px] text-center text-muted-foreground mt-5">Apple login coming with the native app. By continuing you agree to our terms.</p>
        </motion.div>
      </div>
    </div>
  );
}
